import * as Astronomy from "astronomy-engine";
import type { Vec3 } from "./coords";
import { eqjToEcl, type BodyId } from "./positions";
import { assertSupportedDate } from "./time";

export interface RotationState {
  /** 北极方向单位向量（黄道 J2000）。 */
  north: Vec3;
  /**
   * 本初子午线方向单位向量（黄道 J2000）：赤道上经度 0° 的方向。
   * IAU 定义：从北极所在赤道面与 ICRF 赤道的升交点起，沿自转方向转过 W（=spinDeg）。
   * 东方向 = north × primeMeridian。
   */
  primeMeridian: Vec3;
  /** 本初子午线角（度，IAU 约定，0–360）。 */
  spinDeg: number;
}

const ENGINE_BODY: Partial<Record<BodyId, Astronomy.Body>> = {
  sun: Astronomy.Body.Sun, mercury: Astronomy.Body.Mercury, venus: Astronomy.Body.Venus, earth: Astronomy.Body.Earth,
  moon: Astronomy.Body.Moon, mars: Astronomy.Body.Mars, jupiter: Astronomy.Body.Jupiter, saturn: Astronomy.Body.Saturn,
  uranus: Astronomy.Body.Uranus, neptune: Astronomy.Body.Neptune, pluto: Astronomy.Body.Pluto,
};

/**
 * 自转轴与自转相位（IAU 数据，经 astronomy-engine）。
 * 谷神星、妊神星、鸟神星、阋神星没有打包的自转数据，返回 null（界面用默认轴向）。
 */
export function rotationState(id: BodyId, date: Date): RotationState | null {
  assertSupportedDate(date);
  const body = ENGINE_BODY[id];
  if (!body) return null;
  const ax = Astronomy.RotationAxis(body, Astronomy.MakeTime(date));
  const spinDeg = ((ax.spin % 360) + 360) % 360;
  // 在 EQJ 中构造本初子午线：node = ẑ × north（升交点方向），pm = node·cosW + (north × node)·sinW
  const n = ax.north, nodeLen = Math.hypot(n.x, n.y) || 1;
  const node = { x: -n.y / nodeLen, y: n.x / nodeLen, z: 0 };
  const cross = { x: n.y * node.z - n.z * node.y, y: n.z * node.x - n.x * node.z, z: n.x * node.y - n.y * node.x };
  const w = (spinDeg * Math.PI) / 180, c = Math.cos(w), sn = Math.sin(w);
  const pmEqj = new Astronomy.Vector(node.x * c + cross.x * sn, node.y * c + cross.y * sn, node.z * c + cross.z * sn, ax.north.t);
  return { north: eqjToEcl(ax.north), primeMeridian: eqjToEcl(pmEqj), spinDeg };
}
